import { Button, FormControl, makeStyles, Typography } from '@material-ui/core';
import React, { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { colors } from 'theme';
import PercentageInputField from 'components/CustomInputs/PercentageInputField';
import { CancelIcon, ValidateIcon } from 'components/Icons';
import { updateWastageSchema } from './UpdateWastageModal.yup';

const useStyles = makeStyles({
  title: {
    fontSize: '200px',
  },
  maxWastage: {
    fontSize: 16,
    marginBottom: 20,
    color: colors.red,
  },
  buttonLine: {
    display: 'flex',
    marginTop: '25px',
    marginBottom: '15px',
    justifyContent: 'center',
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px',
    margin: '0 10px',
    width: 141,
    height: 38,
    textTransform: 'uppercase',
  },
  icon: {
    fontSize: 'medium',
    fontWeight: 600,
    margin: 6,
  },
});

type UpdateWastageModalProps = {
  maxWastage: number;
  onClose: () => void;
  onSubmit: (newWastage: { wastage: string }) => void;
};

export function formatWastage(wastage?: number, decimals: number = 4): string {
  if (wastage === undefined) return '';
  const multiplier = Math.pow(10, decimals);
  // workaround to prevent "toFixed" from rounding up, thus displaying an upper value
  // that the user cannot copy
  return (Math.floor(wastage * 100 * multiplier) / multiplier).toFixed(decimals) + ' %';
}

export const UpdateWastageModal: React.FC<UpdateWastageModalProps> = ({ maxWastage, onClose, onSubmit }) => {
  const { t } = useTranslation();

  const classes = useStyles();

  const schema = useMemo(() => updateWastageSchema(t, maxWastage, formatWastage(maxWastage)), [t, maxWastage]);

  const { control, handleSubmit, formState, trigger } = useForm({
    mode: 'onChange',
    defaultValues: { wastage: '' },
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    trigger();
  }, [trigger]);

  return (
    <>
      <Typography variant="h1" className={classes.title} style={{ fontSize: 24 }}>
        {t('closureCloseForm.wastage.update.title')}
      </Typography>
      <div className={classes.maxWastage}>
        {t('closureCloseForm.wastage.update.maxWastageMessage', { maxWastage: formatWastage(maxWastage) })}
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormControl>
          <Controller
            name="wastage"
            control={control}
            render={({ field: { ref, ...fieldProps }, fieldState }) => (
              <PercentageInputField
                id="wastage"
                label={t('closureCloseForm.wastage.update.newWastage')}
                variant="outlined"
                inputRef={ref}
                {...fieldProps}
              />
            )}
          />
        </FormControl>
        <div className={classes.buttonLine}>
          <Button variant="outlined" className={classes.button} onClick={onClose}>
            <CancelIcon className={classes.icon} />
            {t('cancel')}
          </Button>
          <Button
            variant="outlined"
            type="submit"
            className={classes.button}
            disabled={!formState.isValid || formState.isSubmitting}
          >
            {t('submit')}
            <ValidateIcon className={classes.icon} />
          </Button>
        </div>
      </form>
    </>
  );
};

export default UpdateWastageModal;
